/**
 * EntryShareButton — per-entry share affordance for the contest.
 *
 * Renders as an icon-only trigger (Share2 from lucide-react) that
 * opens a Popover with four targets:
 *   1. Native share sheet (navigator.share) — mobile-first path
 *   2. Facebook Sharer intent
 *   3. X (Twitter) intent
 *   4. Copy link (Clipboard API, with textarea fallback)
 *
 * Every URL / text goes through `lib/share.js` — the ONE place brand
 * rules and privacy contracts live. Never accepts or emits: email,
 * user_id, address, ZIP, phone, IP, caption.
 *
 * Sizing: tap target ≥44px per session-standing accessibility rule
 * (7df01ba mobile-nav sweep). Two visual variants:
 *   - variant="compact"  (default) — icon-only 44×44, sits next to
 *     the vote control on the /cutest-pluck grid without crowding.
 *   - variant="prominent" — larger with a label, for the post-
 *     submission confirmation card.
 */
import { useState } from "react";
import { Share2, Facebook, Twitter, Copy, Check, Smartphone } from "lucide-react";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";
import {
  buildEntryShareUrl,
  buildEntryShareText,
  buildFacebookShareUrl,
  buildTwitterShareUrl,
  isWebShareSupported,
} from "../lib/share";

function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for older browsers.
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      resolve();
    } catch (e) {
      reject(e);
    }
  });
}

export function EntryShareButton({ entry, variant = "compact" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = buildEntryShareUrl(entry.id);
  const shareText = buildEntryShareText(entry.dog_name);

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: `Vote for ${entry.dog_name}`,
        text: shareText,
        url: shareUrl,
      });
      setOpen(false);
    } catch (e) {
      // User cancelled or share failed — not a real error.
    }
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(`${shareText} ${shareUrl}`);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed \u2014 select and copy manually");
    }
  };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const trigger =
    variant === "prominent" ? (
      <Button
        type="button"
        size="lg"
        className="min-h-[44px] rounded-full bg-brand-orange px-6 text-base font-semibold text-white hover:bg-brand-orange/90"
        data-testid={`entry-share-trigger-prominent-${entry.id}`}
      >
        <Share2 className="mr-2 h-5 w-5" aria-hidden="true" />
        Share &amp; rally votes
      </Button>
    ) : (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Share ${entry.dog_name}'s entry`}
        className="min-h-[44px] min-w-[44px] rounded-full text-pp-text hover:bg-pp-surface-2"
        data-testid={`entry-share-trigger-${entry.id}`}
      >
        <Share2 className="h-5 w-5" aria-hidden="true" />
      </Button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 space-y-1 rounded-2xl border border-border bg-pp-surface p-2 shadow-lg"
        data-testid={`entry-share-menu-${entry.id}`}
      >
        {isWebShareSupported() && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-pp-text hover:bg-pp-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
            data-testid={`entry-share-native-${entry.id}`}
          >
            <Smartphone className="h-4 w-4" aria-hidden="true" />
            Share via device
          </button>
        )}
        <button
          type="button"
          onClick={() => openInNewTab(buildFacebookShareUrl(shareUrl))}
          className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-pp-text hover:bg-pp-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
          data-testid={`entry-share-facebook-${entry.id}`}
        >
          <Facebook className="h-4 w-4" aria-hidden="true" />
          Share on Facebook
        </button>
        <button
          type="button"
          onClick={() => openInNewTab(buildTwitterShareUrl(shareUrl, entry.dog_name))}
          className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-pp-text hover:bg-pp-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
          data-testid={`entry-share-twitter-${entry.id}`}
        >
          <Twitter className="h-4 w-4" aria-hidden="true" />
          Share on X
        </button>
        <button
          type="button"
          onClick={handleCopy}
          className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-pp-text hover:bg-pp-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
          data-testid={`entry-share-copy-${entry.id}`}
        >
          {copied ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          {copied ? "Copied!" : "Copy link"}
        </button>
      </PopoverContent>
    </Popover>
  );
}

export default EntryShareButton;
